import { IWizard, IWizardPage } from '../../../wizard/wizard';
import { ICurrencyEntityReference, IPhotoEntityReference } from '../../../basic';
import { IUiButtonSettings } from '../../../configs/ui-elements';
import { GsStructComponent } from '../component';
import { IMethod } from './../basic/method';

/** The customer loyalty mode configured in the debit card settings */
export enum CustomerLoyaltyMode {
  /** Loyalty is tracked as turnover, rewarded with a bonus and discount levels */
  TurnOver = 0,
  /** Loyalty is tracked as points, convertible into a bonus amount */
  Points = 1
}

/**
 * Value type of a discount level.
 * Mirrors the backend enum `CalulationPrincipleValueType` including its spelling.
 */
export enum CalulationPrincipleValueType {
  /** The value is a percentage */
  Percent = 0,
  /** The value is an absolute amount */
  Value = 1
}

export interface IDebitCardDetailRender {
  /** The wizard data */
  wizard: IWizard;
  /** The debit card detail page */
  page: IWizardPageDebitCard;
  /** Methods available for the debit card detail component */
  methods?: IDebitCardDetailMethods;
  /** Information about the debit card */
  debitCardInfo?: IDebitCardInfo;
  /** URL for the has-no-debit-card page - LinkTarget = hasnodebitcard */
  hasNoDebitCardURL?: string;
}

export interface IWizardPageDebitCard extends IWizardPage {
  /** Image URL for the debit card */
  cardImage?: string;
  /** Link to more information page */
  infoPageLink?: string;
  /** QR code image URL to add to smartphone */
  addToSmartphoneQRCode?: string;
  /** Debit card data */
  debitCard?: IDebitCard;
  /** Example image of a debit card, shown while no card exists yet */
  exampleImage?: IPhotoEntityReference;
  /** Hint shown when a new card was created instead of verifying the existing one */
  debitCardProcessingMessage?: string;
  /** The configured customer loyalty mode, decides whether points or bonus are shown */
  loyaltyMode?: CustomerLoyaltyMode;
  /** Hides the bonus/points box, leaving only the turnover */
  hideBonus?: boolean;
  /** Discount levels, first entry is the current level, second the next one. Only filled in turnover mode */
  discounts?: IDebitCardLevelInfo[];
  /** Methods available for the debit card detail component */
  methods: IDebitCardDetailMethods;
  /** Previous page in the wizard */
  prevPage: IWizardPage | null;
  /** Next page in the wizard */
  nextPage: IWizardPage | null;
}

export interface IDebitCardInfo {
  /** Title of the debit card info section */
  title?: string;
  /** Text of the debit card info section */
  text?: string;
  /** QR code image URL */
  qR?: string;
}

export interface GsStructDebitCardDetailComponent extends GsStructComponent<IDebitCardDetailRender> {
  componentType: 'DebitCardDetail';
  /** ID of the info page */
  infoPageID: string;
  /** Show the page number */
  showNumberPage: boolean;
  /** Show the profile page */
  showProfilePage: boolean;
  /** Hide the "I already have a debit card" option */
  debitCardHideAlreadyHaveADebitCard: boolean;
  /** Hide the bonus/points box, leaving only the turnover */
  hideBonus?: boolean;
  /** RegEx the debit card number is validated against */
  debitCardRegEx?: string;
  /** Custom error message shown when the RegEx validation fails */
  debitCardRegExErrorMessage?: string;
  /** ID of the example picture of a debit card */
  exampleImageID?: number;
  /** Button styling settings */
  buttons?: IUiButtonSettings;
}

export interface IDebitCardDetailMethods {
  /** Method to download the document */
  downloadDocument: IMethod;
  /** Method to add to digital wallet */
  wallet?: IMethod;
  /** Method to go to the next step */
  next?: IMethod;
  /** Method to go to the previous step */
  prev?: IMethod;
}
export interface IDebitCard {
  /** The URL to download the pdf card */
  debitCardID: number;
  /** The debit card number */
  keyValue?: string;
  /** Turnover accumulated on the card */
  turnover?: number;
  /** Bonus earned on the card, in turnover mode the bonus itself, in points mode the monetary value of the points */
  bonus?: number;
  /** Points collected on the card */
  points?: number;
  /** Currency the turnover and bonus are denominated in */
  currency?: ICurrencyEntityReference;
}

/** A single discount level of a debit card */
export interface IDebitCardLevelInfo {
  /** The discount value, either a percentage or an absolute amount depending on the type */
  value: number;
  /** Turnover required to reach this level */
  turnover: number;
  /** Whether the value is a percentage or an absolute amount */
  type: CalulationPrincipleValueType;
}

export interface DownloadResponse {
  data: {
    downloadURL: string;
  };
}

export interface WalletResponse {
  /** The file contents as a base64 encoded string */
  fileContents?: string;
  /** The content type of the file */
  contentType?: string;
  /** The suggested file name for download */
  fileDownloadName?: string;
}
